import { useState, useEffect, useMemo, useCallback } from 'react';
import superagent from 'superagent';

import {
  Chart as ChartJS,
} from 'chart.js';
import { backendUrl } from '@helpers/consts';

import GrowthRateModal from './components/GrowthRateModal';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ForecastHeader from './components/ForecastHeader';
import FilterSection from './components/FilterSection';
import SummaryCards from './components/SummaryCards';
import ChartCard from './components/ChartCard';
import DetailedTable from './components/DetailedTable';
import StatusSummary from './components/StatusSummary';
import BudgetForecastTable from './components/BudgetForecastTable';


const valueOnTopPlugin = {
  id: 'valueOnTop',
  afterDatasetsDraw: (chart) => {

    if (chart.options.plugins?.valueOnTop === true) {
      
    

    const { ctx, data } = chart;
    ctx.save();
    ctx.font = 'bold 11px Arial';
    ctx.fillStyle = '#374151';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value != null && value !== 0) {
          const x = bar.x;
          const y = bar.y - 5; // Position above the bar
          ctx.fillText(value.toFixed(2), x, y);
        }
      });
    });
    ctx.restore();
    }
  }
};


ChartJS.register(
  valueOnTopPlugin
);


const fadeInStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }
`;


if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = fadeInStyles;
  document.head.appendChild(styleSheet);
}


const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const calculateInventoryStatus = (currentStock, totalFutureForecast, futureMonths) => {
  let monthCover = 0;
  let monthCoverDisplay = 'N/A';
  let status = 'Zero Forecast';
  
  
  if (currentStock <= 0) {
  
    
    if (totalFutureForecast > 0 && futureMonths.length > 0) {
      status = 'Out of Stock';
      monthCover = currentStock / (totalFutureForecast / futureMonths.length);
      monthCoverDisplay = monthCover.toFixed(2);
    } else {
      status = 'Zero Forecast';
      monthCover = 0;
      monthCoverDisplay = '0.00';
    }
  } else if (totalFutureForecast === 0) {
    
    status = 'Zero Forecast';
    monthCover = 0;
    monthCoverDisplay = 'N/A';
  } else {
    
    monthCover = currentStock / (totalFutureForecast / futureMonths.length);
    monthCoverDisplay = monthCover.toFixed(2);
    
    if (monthCover < 0.5) status = 'Shortage';
    else if (monthCover < 1) status = 'Fast Moving';
    else if (monthCover <= 3) status = 'Ideal';
    else if (monthCover <= 5) status = 'Slow Moving';
    else status = 'Excess';
  }
  
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Out of Stock': return 'text-red-600 bg-red-50';
      case 'Zero Forecast': return 'text-gray-600 bg-gray-50';
      case 'Shortage': return 'text-orange-600 bg-orange-50';
      case 'Fast Moving': return 'text-yellow-600 bg-yellow-50';
      case 'Ideal': return 'text-green-600 bg-green-50';
      case 'Slow Moving': return 'text-blue-600 bg-blue-50';
      case 'Excess': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };
  
  return {
    status,
    monthCover,
    monthCoverDisplay,
    statusObject: { label: status, color: getStatusColor(status) }
  };
};


const statusOrder = {
  'Out of Stock': 1,
  'Shortage': 2,
  'Fast Moving': 3,
  'Ideal': 4,
  'Slow Moving': 5,
  'Excess': 6,
  'Zero Forecast': 7,
};

const ForecastMonthStock = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [viewType, setViewType] = useState('category');
  const [selectedRegion, setSelectedRegion] = useState('AU');
  const [selectedStatusTab, setSelectedStatusTab] = useState("formula1");
  
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [isGrowthRateModalOpen, setIsGrowthRateModalOpen] = useState(false);
  const [futureForecastData, setFutureForecastData] = useState(null);
  const [futureForecastLoading, setFutureForecastLoading] = useState(false);

  // Backend Filter states - Replace local filtering logic
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedCategoryNames, setSelectedCategoryNames] = useState([]);

  
  const [searchTerm, setSearchTerm] = useState('');

  
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  
  const requestSort = useCallback((key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build payload with backend filters
      const payload = { 
        region: selectedRegion 
      };
      
      // Add backend filters if they exist
      if (selectedProductIds.length > 0) {
        payload.productIds = selectedProductIds;
      }
      
      if (selectedCategoryNames.length > 0) {
        payload.categoryNames = selectedCategoryNames;
      }
      
      const response = await superagent
        .post(`${backendUrl}/reports/calculateExpectedMonthsCover`)
        .send(payload)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      setForecastData(response.body);
      
      if (response.body && response.body.bySku && response.body.bySku[selectedRegion]) {
        const skusInRegion = Object.values(response.body.bySku[selectedRegion]);
        if (skusInRegion.length > 0 && skusInRegion[0].monthData) {
          setSelectedMonths(Object.keys(skusInRegion[0].monthData).slice(0, 4));
        }
      }
      
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch forecast data');
      setForecastData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedRegion, selectedProductIds, selectedCategoryNames]);

  const fetchFutureForecast = useCallback(async () => {
    try {
      setFutureForecastLoading(true);
      
      // Build query with backend filters
      const query = { 
        region: selectedRegion 
      };
      
      // Add backend filters if they exist
      if (selectedProductIds.length > 0) {
        query.productIds = selectedProductIds.join(',');
      }
      
      if (selectedCategoryNames.length > 0) {
        query.categoryNames = selectedCategoryNames.join(',');
      }
      
      const response = await superagent
        .get(`${backendUrl}/reports/future-forecast`)
        .query(query)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      setFutureForecastData(response.body);
      
    } catch (err) {
      console.error('Failed to fetch future forecast data:', err);
    } finally {
      setFutureForecastLoading(false);
    }
  }, [selectedRegion, selectedProductIds, selectedCategoryNames]);

  // Handler for FilterSection
  const handleFiltersChange = useCallback(({ productIds, categoryNames }) => {
    setSelectedProductIds(productIds || []);
    setSelectedCategoryNames(categoryNames || []);
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchData(), fetchFutureForecast()]);
  }, [fetchData, fetchFutureForecast]);

  useEffect(() => {
    fetchData();
    fetchFutureForecast();
  }, [selectedRegion, selectedProductIds, selectedCategoryNames]);

  const chartData = useMemo(() => {
    if (!forecastData || !selectedMonths || selectedMonths.length === 0) return null;
    
    let data;
    let labels = [];
    let datasets = [];
    
    const calculateMonthCover = (item) => {
      const futureMonths = selectedMonths.filter(month => {
        const monthData = item.monthData && item.monthData[month] || {};
        return !monthData.isPastMonth;
      });
      
      let totalFutureForecast = 0;
      futureMonths.forEach(month => {
        const monthData = item.monthData && item.monthData[month] || {};
        totalFutureForecast += monthData.quantity || 0;
      });
      
      if (totalFutureForecast > 0 && futureMonths.length > 0) {
        return (item.currentStock || 0) / (totalFutureForecast / futureMonths.length);
      }
      return 0;
    };
    
    let dataSource;
    if (viewType === 'category' && forecastData.byCategory?.[selectedRegion]) {
      dataSource = Object.values(forecastData.byCategory[selectedRegion]);
      labels = dataSource.map(item => item.categoryName);
    } else if (viewType === 'product' && forecastData.byProduct?.[selectedRegion]) {
      dataSource = Object.values(forecastData.byProduct[selectedRegion]);
      labels = dataSource.map(item => item.productName);
    } else if (viewType === 'sku' && forecastData.bySku?.[selectedRegion]) {
      dataSource = Object.entries(forecastData.bySku[selectedRegion])
        .map(([sku, item]) => ({ sku, ...item }))
        .slice(0, 30);
      labels = dataSource.map(item => item.sku);
    }

    if (dataSource) {
      const monthCovers = dataSource.map(item => calculateMonthCover(item));
      datasets.push({
        label: 'Month Cover',
        data: monthCovers,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      });
    }
    
    return { labels, datasets };
  }, [forecastData, viewType, selectedMonths, selectedRegion]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25 // Add padding to show labels above bars
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Months Cover' }
      },
      x: {
        ticks: { maxRotation: 45, minRotation: 45 },
        title: { display: true, text: viewType === 'category' ? 'Category' : viewType === 'product' ? 'Product' : 'SKU' }
      }
    },
    plugins: {
      valueOnTop: true,
      title: { display: true, text: 'Month Cover Analysis', font: { size: 16 } },
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `Month Cover: ${context.parsed.y.toFixed(2)} months`
        }
      }
    },
    barPercentage: viewType === 'sku' ? 0.8 : 0.6,
    categoryPercentage: viewType === 'sku' ? 0.9 : 0.8
  }), [viewType]);
  
  const upcomingChartData = useMemo(() => {
    if (!forecastData || !selectedMonths || selectedMonths.length === 0) return null;
    
    let labels = [];
    let datasets = [];
    
    const selectedMonthsFuture = selectedMonths.filter(month => {
        const monthDate = new Date(month);
        const currentDate = new Date();
        currentDate.setHours(0,0,0,0);
        return monthDate >= currentDate;
    });

    let dataSource;
    if (viewType === 'category' && forecastData.byCategory?.[selectedRegion]) {
        dataSource = Object.values(forecastData.byCategory[selectedRegion]);
        labels = dataSource.map(item => item.categoryName);
    } else if (viewType === 'product' && forecastData.byProduct?.[selectedRegion]) {
        dataSource = Object.values(forecastData.byProduct[selectedRegion]);
        labels = dataSource.map(item => item.productName);
    } else if (viewType === 'sku' && forecastData.bySku?.[selectedRegion]) {
        dataSource = Object.entries(forecastData.bySku[selectedRegion])
            .map(([sku, item]) => ({ sku, ...item }))
            .slice(0, 20);
        labels = dataSource.map(item => item.sku);
    }

    if (dataSource) {
        selectedMonthsFuture.forEach((month, index) => {
            datasets.push({
                label: `${month} Upcoming`,
                data: dataSource.map(item => item.monthData?.[month]?.upcomingTotal || 0),
                backgroundColor: `rgba(${255 - index * 50}, ${99 + index * 30}, ${132 + index * 20}, 0.6)`,
                borderColor: `rgba(${255 - index * 50}, ${99 + index * 30}, ${132 + index * 20}, 1)`,
                borderWidth: 1,
            });
            datasets.push({
                label: `${month} Forecast`,
                data: dataSource.map(item => item.monthData?.[month]?.quantity || 0),
                backgroundColor: `rgba(${75 + index * 50}, ${192 - index * 30}, ${192 - index * 20}, 0.6)`,
                borderColor: `rgba(${75 + index * 50}, ${192 - index * 30}, ${192 - index * 20}, 1)`,
                borderWidth: 1,
            });
        });
    }
    
    return { labels, datasets };
  }, [forecastData, viewType, selectedMonths, selectedRegion]);

  const upcomingChartOptions = {
    
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25 
      }
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Quantity' } },
      x: { ticks: { maxRotation: 45, minRotation: 45 } }
    },
    plugins: {
      valueOnTop: true,
      title: { display: true, text: 'Forecast and Upcoming Inventory', font: { size: 16 } },
      legend: { position: 'top' },
      
    }
  };
  
  
  
  const tableData = useMemo(() => {
    if (!forecastData || !selectedMonths || selectedMonths.length === 0) return [];
    
    let dataSource;
    switch(viewType) {
      case 'category': dataSource = forecastData.byCategory?.[selectedRegion] || {}; break;
      case 'product': dataSource = forecastData.byProduct?.[selectedRegion] || {}; break;
      case 'sku': default: dataSource = forecastData.bySku?.[selectedRegion] || {};
    }

    return Object.entries(dataSource).map(([key, item]) => {
      const baseData = {
        key,
        currentStock: item.currentStock || 0,
        stockOutDateFormatted: item.stockOutDateFormatted || '-',
      };
      
      const futureMonths = selectedMonths.filter(month => !item.monthData?.[month]?.isPastMonth);
      const totalFutureForecast = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.quantity || 0), 0);
      const currentStock = item.currentStock || 0;
      
      const inventoryCalc = calculateInventoryStatus(currentStock, totalFutureForecast, futureMonths);
      
      const monthCoverDisplay = inventoryCalc.monthCoverDisplay;
      
      if (totalFutureForecast > 0 && futureMonths.length > 0) {
        
        const totalDays = futureMonths.reduce((acc, month) => {
          const date = new Date(month);
          return acc + getDaysInMonth(date.getFullYear(), date.getMonth());
        }, 0);
       
        if (totalDays > 0) {
            const dailyForecastRate = totalFutureForecast / totalDays;
            if (dailyForecastRate > 0) {
                const totalUpcomingStock = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.upcomingTotal || 0), 0);
                const adjustedStock = (item.currentStock || 0) + totalUpcomingStock;
                const daysUntilStockOut = Math.floor(adjustedStock / dailyForecastRate);
            
                if (daysUntilStockOut > 0) {
                  const stockOutDate = new Date();
                  stockOutDate.setDate(stockOutDate.getDate() + daysUntilStockOut);
                  baseData.calculatedStockOutDate = stockOutDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                  baseData.sortableStockOutDate = stockOutDate; 
                } else {
                  baseData.calculatedStockOutDate = '-';
                  baseData.sortableStockOutDate = null;
                }
            } else {
                baseData.calculatedStockOutDate = 'N/A';
                baseData.sortableStockOutDate = null;
            }
        } else {
             baseData.calculatedStockOutDate = 'N/A';
             baseData.sortableStockOutDate = null;
        }
      } else {
        baseData.calculatedStockOutDate = 'N/A';
        baseData.sortableStockOutDate = null;
      }
      
      
      baseData.calculatedMonthCover = monthCoverDisplay;
      
      baseData.inventoryStatus = inventoryCalc.statusObject;
      
      selectedMonths.forEach(month => {
        const monthData = item.monthData?.[month] || {};
        baseData[`${month}_forecast`] = monthData.quantity || 0;
        baseData[`${month}_actual`] = monthData.actualQuantity ?? null;
        baseData[`${month}_mtdSales`] = monthData.mtdSales ?? null;
        baseData[`${month}_mtdDaysPassed`] = monthData.mtdDaysPassed ?? null;
        
        if (monthData.actualQuantity != null && monthData.quantity) {
          const accuracy = 100 - Math.abs((monthData.actualQuantity - monthData.quantity) / monthData.quantity * 100);
          baseData[`${month}_accuracy`] = accuracy > 0 ? accuracy.toFixed(1) : 0;
        } else {
          baseData[`${month}_accuracy`] = null;
        }
        
        baseData[`${month}_upcoming`] = monthData.upcomingTotal || 0;
        baseData[`${month}_upcomingDetails`] = monthData.upcomingDetails || [];
      });
      
      switch(viewType) {
        case 'category': baseData.name = item.categoryName; break;
        case 'product': baseData.name = item.productName; baseData.category = item.category; break;
        case 'sku': baseData.sku = key; baseData.productName = item.productName; baseData.category = item.category; break;
      }
      
      return baseData;
    });
  }, [forecastData, viewType, selectedMonths, selectedRegion]);

  const filteredAndSortedTableData = useMemo(() => {
    let filtered = [...tableData];

    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.sku?.toLowerCase().includes(searchLower) ||
        item.name?.toLowerCase().includes(searchLower) ||
        item.productName?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower)
      );
    }

    
    
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if(sortConfig.key === 'inventoryStatus') {
          const aStatus = statusOrder[aValue.label] || 999;
          const bStatus = statusOrder[bValue.label] || 999;
          
          if (sortConfig.direction === 'ascending') {
            return aStatus - bStatus;
          } else {
            return bStatus - aStatus;
          }
        }
        if (sortConfig.key === 'currentStock' || sortConfig.key === 'calculatedMonthCover') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortConfig.key === 'sortableStockOutDate') {
          
          if (aValue === null) return 1;
          if (bValue === null) return -1;
          if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }

         else {
          
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [tableData, searchTerm, viewType, sortConfig]);

  const summaryData = useMemo(() => {
    if (!forecastData || !selectedMonths || selectedMonths.length === 0 || !forecastData.bySku?.[selectedRegion]) {
        return { monthlyForecast: {} };
    }
    
    const summary = {
      totalCurrentStock: 0,
      totalUpcomingStock: 0,
      monthlyForecast: {},
      overallMonthCover: 0,
    };
    
    selectedMonths.forEach(month => {
      summary.monthlyForecast[month] = {
        forecastTotal: 0,
        upcomingTotal: 0,
        actualTotal: 0,
        totalAccuracy: 0,
        itemsWithAccuracy: 0,
        mtdSalesTotal: 0,
        hasMtdData: false
      };
    });
    
    const skuItems = Object.values(forecastData.bySku[selectedRegion]);
    summary.totalCurrentStock = skuItems.reduce((acc, item) => acc + (item.currentStock || 0), 0);

    let totalFutureForecast = 0;
    
    skuItems.forEach(item => {
      selectedMonths.forEach(month => {
        const monthData = item.monthData?.[month] || {};
        if (!monthData.isPastMonth) {
            summary.monthlyForecast[month].forecastTotal += monthData.quantity || 0;
            summary.monthlyForecast[month].upcomingTotal += monthData.upcomingTotal || 0;
            summary.totalUpcomingStock += monthData.upcomingTotal || 0;
            totalFutureForecast += monthData.quantity || 0;
        }
        
        if (monthData.mtdSales != null) {
          summary.monthlyForecast[month].mtdSalesTotal += monthData.mtdSales;
          summary.monthlyForecast[month].hasMtdData = true;
          summary.monthlyForecast[month].mtdDaysPassed = monthData.mtdDaysPassed;
        }
        
        if (monthData.actualQuantity != null) {
          summary.monthlyForecast[month].actualTotal += monthData.actualQuantity;
          if (monthData.quantity) {
            const accuracy = 100 - Math.abs((monthData.actualQuantity - monthData.quantity) / monthData.quantity * 100);
            summary.monthlyForecast[month].totalAccuracy += accuracy > 0 ? accuracy : 0;
            summary.monthlyForecast[month].itemsWithAccuracy++;
          }
        }
      });
    });

    const currentMonth = selectedMonths.find(m => { 
      const currentDate = new Date();
      const d = new Date(m);
      return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
    });

    
    
    const futureMonthsCount = selectedMonths.filter(m => !forecastData.bySku[selectedRegion][skuItems[0]?.sku || Object.keys(forecastData.bySku[selectedRegion])[0]]?.monthData?.[m]?.isPastMonth).length;
    if (totalFutureForecast > 0 && futureMonthsCount > 0) {
      summary.overallMonthCover = (summary.totalCurrentStock - summary.monthlyForecast[currentMonth].forecastTotal  ) / (totalFutureForecast / futureMonthsCount);
    }
    
    selectedMonths.forEach(month => {
      const monthSummary = summary.monthlyForecast[month];
      if (monthSummary.itemsWithAccuracy > 0) {
        monthSummary.averageAccuracy = monthSummary.totalAccuracy / monthSummary.itemsWithAccuracy;
      } else {
        monthSummary.averageAccuracy = null;
      }
    });
    
    return summary;
  }, [forecastData, selectedMonths, selectedRegion]);

  const futureForecastChartData = useMemo(() => {
    if (!futureForecastData?.totalByMonth) return null;
    
    const months = Object.keys(futureForecastData.totalByMonth);
    if (months.length === 0) return null;
    
    const categoryColors = {
      'Bags': 'rgba(255, 99, 132, 0.7)', 'Luggage': 'rgba(54, 162, 235, 0.7)',
      'Accessories': 'rgba(255, 206, 86, 0.7)', 'Spare Parts': 'rgba(75, 192, 192, 0.7)',
       'Uncategorized': 'rgba(255, 159, 64, 0.7)',
      'Cygnett': 'rgba(199, 199, 199, 0.7)'
    };

    const activeCategories = Object.keys(futureForecastData.categories || {})
      .filter(category => Object.values(futureForecastData.categories[category] || {}).some(v => v > 0));
    
    const datasets = activeCategories.map(category => ({
      label: category,
      data: months.map(month => futureForecastData.categories[category][month] || 0),
      backgroundColor: categoryColors[category] || `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.7)`,
      borderWidth: 1
    }));
    
    datasets.unshift({
      label: 'Total',
      data: months.map(month => futureForecastData.totalByMonth[month]),
      type: 'line',
      fill: false,
      borderColor: 'rgba(0, 0, 0, 0.7)',
      borderWidth: 2,
      yAxisID: 'y1'
    });
    
    return { labels: months, datasets };
  }, [futureForecastData]);

  const futureForecastChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Quantity (per category)' } },
      y1: { position: 'right', beginAtZero: true, title: { display: true, text: 'Total Quantity' }, grid: { display: false } },
      x: { stacked: true, title: { display: true, text: 'Month' } }
    },
    plugins: {
      valueOnTop: false,
      title: { display: true, text: 'Future Forecast by Category', font: { size: 16 } },
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label || ''}: ${context.parsed.y.toLocaleString()}`
        }
      }
    }
  };

  const statusSummaryData = useMemo(() => {
    if (!forecastData?.bySku?.[selectedRegion] || !selectedMonths.length) return [];
    
    const statusGroups = { 'Zero Forecast': [], 'Out of Stock': [], 'Shortage': [], 'Fast Moving': [], 'Ideal': [], 'Slow Moving': [], 'Excess': [] };
    const skuItems = Object.entries(forecastData.bySku[selectedRegion]);
    
    skuItems.forEach(([skuCode, item]) => {
      const futureMonths = selectedMonths.filter(month => !item.monthData?.[month]?.isPastMonth);
      const totalFutureForecast = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.quantity || 0), 0);
      const currentStock = item.currentStock || 0;
      
      
      const inventoryCalc = calculateInventoryStatus(currentStock, totalFutureForecast, futureMonths);
      const status = inventoryCalc.status;
      
      const totalUpcomingStock = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.upcomingTotal || 0), 0);
      statusGroups[status].push({
        sku: skuCode,
        category: item.category || 'Unknown',
        productName: item.productName || 'Unknown',
        soh: currentStock,
        upcoming: totalUpcomingStock
      });
    });
    
    return Object.entries(statusGroups).map(([status, skus]) => ({
      status,
      skus,
      count: skus.length,
      percentage: (skus.length / skuItems.length * 100).toFixed(0),
      soh: skus.reduce((acc, sku) => acc + sku.soh, 0),
      upcomingTotal: skus.reduce((acc, sku) => acc + sku.upcoming, 0)
    }));
  }, [forecastData, selectedMonths, selectedRegion]);

  const statusSummaryData2 = useMemo(() => {
    if (!forecastData?.bySku?.[selectedRegion] || !selectedMonths.length) return [];
    
    const statusGroups = { 'Zero Forecast': [], 'Out of Stock': [], 'Shortage': [], 'Fast Moving': [], 'Ideal': [], 'Slow Moving': [], 'Excess': [] };
    const skuItems = Object.entries(forecastData.bySku[selectedRegion]);
    const currentDate = new Date();
    const currentMonthStr = selectedMonths.find(m => {
        const d = new Date(m);
        return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
    });

    skuItems.forEach(([skuCode, item]) => {
      const futureMonths = selectedMonths.filter(month => !item.monthData?.[month]?.isPastMonth);
      const totalFutureForecast = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.quantity || 0), 0);
      const totalUpcomingStock = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.upcomingTotal || 0), 0);
      const currentMonthForecast = item.monthData?.[currentMonthStr]?.quantity || 0;
      const currentStock = item.currentStock || 0;

      // Calculate adjusted stock for formula2
      const adjustedStock = currentStock + totalUpcomingStock - currentMonthForecast;
      
      
      const inventoryCalc = calculateInventoryStatus(adjustedStock, totalFutureForecast, futureMonths);
      const status = inventoryCalc.status;
      
      statusGroups[status].push({
        sku: skuCode,
        category: item.category || 'Unknown',
        productName: item.productName || 'Unknown',
        soh: currentStock,
        upcoming: totalUpcomingStock
      });
    });
    
    return Object.entries(statusGroups).map(([status, skus]) => ({
      status,
      skus,
      count: skus.length,
      percentage: (skus.length / skuItems.length * 100).toFixed(0),
      soh: skus.reduce((acc, sku) => acc + sku.soh, 0),
      upcomingTotal: skus.reduce((acc, sku) => acc + sku.upcoming, 0)
    }));
  }, [forecastData, selectedMonths, selectedRegion]);

  return (
    <div className="page-content">
      <div className="container mx-auto px-4 lg:max-w-[85%]">
      
        
        <ForecastHeader />
        
        <FilterSection
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          viewType={viewType}
          setViewType={setViewType}
          selectedProductIds={selectedProductIds}
          selectedCategoryNames={selectedCategoryNames}
          onFiltersChange={handleFiltersChange}
          loading={loading}
          onOpenGrowthRateModal={() => setIsGrowthRateModalOpen(true)}
        />
        
        <SummaryCards
          loading={loading}
          summaryData={summaryData}
          selectedRegion={selectedRegion}
          selectedMonths={selectedMonths}
        />
        
        <ChartCard
          title={
            viewType === 'category' ? 'Month Cover by Category' : 
            viewType === 'product' ? 'Month Cover by Product' : 
            'Month Cover by SKU'
          }
          loading={loading}
          error={error}
          chartData={chartData}
          chartOptions={chartOptions}
          viewType={viewType}
        />
        
        <ChartCard
          title="Forecast vs Upcoming Inventory"
          loading={loading}
          error={error}
          chartData={upcomingChartData}
          chartOptions={upcomingChartOptions}
          viewType={viewType}
        />
        
        <DetailedTable
          loading={loading}
          error={error}
          filteredAndSortedTableData={filteredAndSortedTableData}
          tableData={tableData}
          forecastData={forecastData}
          selectedRegion={selectedRegion}
          viewType={viewType}
          selectedMonths={selectedMonths}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortConfig={sortConfig}
          requestSort={requestSort}
        />
        
        <StatusSummary
          loading={loading}
          error={error}
          statusSummaryData={statusSummaryData}
          statusSummaryData2={statusSummaryData2}
          selectedStatusTab={selectedStatusTab}
          setSelectedStatusTab={setSelectedStatusTab}
          selectedRegion={selectedRegion}
        />
        
        <BudgetForecastTable
          loading={futureForecastLoading}
          futureForecastData={futureForecastData}
          selectedRegion={selectedRegion}
          futureForecastChartData={futureForecastChartData}
          futureForecastChartOptions={futureForecastChartOptions}
        />
        
        <GrowthRateModal
          isOpen={isGrowthRateModalOpen}
          onClose={() => setIsGrowthRateModalOpen(false)}
          onDataRefresh={refreshData}
          region={selectedRegion}
        />
        
        <ToastContainer />
      </div>
    </div>
  );
};

export default ForecastMonthStock;
